import { useCallback, useEffect, useState } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  type InfiniteData,
} from '@tanstack/react-query';
import { useSubscription } from '@trpc/tanstack-react-query';
import { Hub } from 'aws-amplify/utils';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  type DownloadListItem,
  DownloadItemStatus,
  DownloadItemType,
  GeneratedDocumentItemStatus,
} from '../types';
import { trpc, queryClient } from '@/utils/trpc';

const PAGE_SIZE = 20;

type ListDownloadsPage = {
  items: DownloadListItem[];
  countUnread: number;
  totalCount: number;
};

const mapGeneratedDocumentStatus = (
  status: GeneratedDocumentItemStatus,
): DownloadItemStatus => {
  switch (status) {
    case GeneratedDocumentItemStatus.Queued:
      return DownloadItemStatus.QUEUED;
    case GeneratedDocumentItemStatus.Generating:
      return DownloadItemStatus.GENERATING;
    case GeneratedDocumentItemStatus.Ready:
      return DownloadItemStatus.READY;
    case GeneratedDocumentItemStatus.Failed:
    case GeneratedDocumentItemStatus.Unspecified:
      return DownloadItemStatus.FAILED;
  }
};

export const useListDownloads = (enabled: boolean = true) => {
  return useInfiniteQuery({
    enabled,
    queryKey: trpc.downloadsRouter.getDownloadItems.queryKey(),
    queryFn: async ({ pageParam = 0 }) => {
      const result = await queryClient.fetchQuery(
        trpc.downloadsRouter.getDownloadItems.queryOptions({
          pagination: { offset: pageParam, limit: PAGE_SIZE },
        }),
      );

      const items: DownloadListItem[] = [
        ...result.items.map((item) => ({
          ...item,
          itemType: DownloadItemType.DownloadItem as const,
        })),
        ...(result.generatedDocuments ?? []).map((doc) => ({
          ...doc,
          status: mapGeneratedDocumentStatus(doc.status),
          itemType: DownloadItemType.GeneratedDocument as const,
        })),
      ].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
      return {
        items,
        countUnread: result.countUnread,
        totalCount: result.totalCount,
      };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      const loaded = allPages.reduce((sum, page) => sum + page.items.length, 0);
      return loaded < lastPage.totalCount
        ? lastPageParam + PAGE_SIZE
        : undefined;
    },
  });
};

export const useMarkDownloadItemAsRead = () => {
  return useMutation({
    ...trpc.downloadsRouter.markDownloadItemAsRead.mutationOptions(),
    onSuccess: (_data, { documentId }) => {
      queryClient.setQueryData<InfiniteData<ListDownloadsPage>>(
        trpc.downloadsRouter.getDownloadItems.queryKey(),
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page, index) => ({
              ...page,
              ...(index === 0
                ? { countUnread: Math.max(0, page.countUnread - 1) }
                : {}),
              items: page.items.map((item) =>
                item.id === documentId ? { ...item, isUnread: false } : item,
              ),
            })),
          };
        },
      );
    },
  });
};

export const useMarkAllDownloadItemsAsRead = () => {
  return useMutation({
    ...trpc.downloadsRouter.markAllDownloadItemsAsRead.mutationOptions(),
    onSuccess: () => {
      queryClient.setQueryData<InfiniteData<ListDownloadsPage>>(
        trpc.downloadsRouter.getDownloadItems.queryKey(),
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map((page, index) => ({
              ...page,
              ...(index === 0 ? { countUnread: 0 } : {}),
              items: page.items.map((item) => ({ ...item, isUnread: false })),
            })),
          };
        },
      );
      toast.success('All downloads marked as read');
    },
  });
};

export const useClearUserDownloads = () => {
  return useMutation({
    ...trpc.downloadsRouter.clearUserDownloads.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: trpc.downloadsRouter.getDownloadItems.queryKey(),
      });
    },
  });
};

export const useGetDownloadItemLink = (
  documentId: string,
  patientId: string,
  itemType: DownloadItemType,
) => {
  return useQuery({
    ...trpc.downloadsRouter.getDownloadItemLink.queryOptions({
      documentId,
      patientId,
      itemType,
    }),
    enabled: false,
  });
};

export const useGetDownloadAvailableParameters = (
  patientId: string,
  startDate?: Date,
  endDate?: Date,
  options?: {
    enabled?: boolean;
  },
) => {
  return useQuery({
    ...trpc.downloadsRouter.getDownloadAvailableParameters.queryOptions({
      patientId,
      startDate: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
      endDate: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
    }),
    enabled: (options?.enabled ?? true) && !!patientId,
  });
};

export const useSubmitDownloadRegenerateRequest = () => {
  return useMutation({
    ...trpc.downloadsRouter.submitDownloadRegenerateRequest.mutationOptions(),
    onSuccess: (updatedItem) => {
      const updatedListItem: DownloadListItem =
        'kind' in updatedItem
          ? {
              ...updatedItem,
              status: mapGeneratedDocumentStatus(updatedItem.status),
              itemType: DownloadItemType.GeneratedDocument,
            }
          : { ...updatedItem, itemType: DownloadItemType.DownloadItem };

      queryClient.setQueryData<InfiniteData<ListDownloadsPage>>(
        trpc.downloadsRouter.getDownloadItems.queryKey(),
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              items: page.items.map((item) =>
                item.id === updatedListItem.id ? updatedListItem : item,
              ),
            })),
          };
        },
      );
    },
  });
};

export const useSubmitDownloadRequest = () => {
  return useMutation({
    ...trpc.downloadsRouter.submitDownloadRequest.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: trpc.downloadsRouter.getDownloadItems.queryKey(),
      });
      toast('Download successfully requested.', {
        description:
          'Your file will appear in the "Downloads" area when it is ready.',
      });
    },
    onError: () => {
      toast.error('Download request failed.', {
        description: 'Please try again.',
      });
    },
  });
};

export const useCreateGeneratedDocument = () => {
  return useMutation({
    ...trpc.downloadsRouter.createGeneratedDocument.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: trpc.downloadsRouter.getDownloadItems.queryKey(),
      });
      toast('Download successfully requested.', {
        description:
          'Your file will appear in the "Downloads" area when it is ready.',
      });
    },
  });
};

export const useDownloadsUpdates = () => {
  const [hasUpdates, setHasUpdates] = useState(false);

  const subscription = useSubscription(
    trpc.downloadsRouter.getDownloadsUpdates.subscriptionOptions(undefined, {
      onData: () => {
        setHasUpdates(true);
      },
    }),
  );

  useEffect(() => {
    const unlisten = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'tokenRefresh' || payload.event === 'signedIn') {
        subscription.reset();
      }
    });
    return unlisten;
  }, [subscription]);

  const dismissUpdates = useCallback(() => {
    setHasUpdates(false);
    queryClient.invalidateQueries({
      queryKey: trpc.downloadsRouter.getDownloadItems.queryKey(),
    });
  }, []);

  return { hasUpdates, dismissUpdates };
};
